import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, Moon, Palette, Building2, Trees, Anchor } from "lucide-react";

const URL = "https://whatfinland.com/guides/helsinki-neighborhoods";
const TITLE = "Helsinki Neighborhood Guide: Where to Stay & Explore (2026)";
const DESC =
  "Where to stay in Helsinki — Punavuori for design, Kallio for nightlife, Töölö for culture, Kruununhaka for old-town calm. A local's neighborhood guide with hotels, cafés and what each area is actually like.";
const COVER = "https://whatfinland.com/og/helsinki-neighborhoods.jpg";

type Hood = {
  name: string;
  tagline: string;
  vibe: string;
  stayFor: string;
  avoidIf: string;
  highlights: string[];
  icon: React.ComponentType<{ className?: string }>;
};

const HOODS: Hood[] = [
  {
    name: "Punavuori",
    tagline: "Design District — boutiques, galleries, slow cafés",
    vibe: "Walkable grid of 19th-century blocks. Independent fashion, ceramics studios, third-wave coffee. Quieter than the centre, livelier than the suburbs.",
    stayFor: "First-timers who want the city centre without the chain-hotel feel. Walkable to Market Square, Löyly and the sea.",
    avoidIf: "You want a pool, a gym and a buffet breakfast — Punavuori does boutique, not big-box.",
    highlights: ["Design Museum", "Hietalahti food hall", "Johan & Nyström coffee", "Bulevardi for late-night walks"],
    icon: Palette,
  },
  {
    name: "Kallio",
    tagline: "Nightlife, dive bars, post-industrial cool",
    vibe: "Helsinki's Brooklyn — students, artists, drag bars, natural-wine bistros, Turkish grills and the city's best dive bars. Loud Friday, mellow Sunday.",
    stayFor: "Bar-hopping, live music, eating cheap and well. Best metro connection to anywhere.",
    avoidIf: "You're a light sleeper and book a room above Vaasankatu.",
    highlights: ["Kotiharjun Sauna", "Siltanen", "Roslund", "Kallio church viewpoint"],
    icon: Moon,
  },
  {
    name: "Töölö",
    tagline: "Culture, parks, embassies, Sibelius",
    vibe: "Wide art-nouveau boulevards, lake walks, classical concerts. Calmer and more residential — old Helsinki money lives here.",
    stayFor: "Museum-goers and concert-goers. Walking distance to Oodi, Musiikkitalo, Finlandia Hall and Töölönlahti Bay.",
    avoidIf: "You want late-night life — Töölö goes to bed early.",
    highlights: ["Sibelius Monument", "Helsinki Music Centre", "Kansallismuseo", "Café Regatta"],
    icon: Building2,
  },
  {
    name: "Kruununhaka",
    tagline: "Old town — cathedrals, cobblestones, embassies",
    vibe: "The original Helsinki: pastel neoclassical blocks behind the cathedral, antique shops, tiny wine bars. Almost no traffic.",
    stayFor: "Photographers and slow travellers. Two minutes to Market Square and the Suomenlinna ferry.",
    avoidIf: "You want shopping or nightlife on your doorstep.",
    highlights: ["Helsinki Cathedral", "Katajanokka art-nouveau blocks", "Savotta", "Suomenlinna ferry"],
    icon: Anchor,
  },
  {
    name: "Kamppi & Centre",
    tagline: "Trains, trams, department stores, hotels",
    vibe: "Glass-and-steel central — Kamppi bus station, Stockmann, the main tram hub. Functional, not pretty, but everything is 5 minutes away.",
    stayFor: "Short stays, business trips, anyone arriving by train or flying out early via the airport train.",
    avoidIf: "You came for character — the centre is the least Helsinki-feeling part of Helsinki.",
    highlights: ["Kamppi Chapel", "Amos Rex", "Central Station", "Forum & Kamppi malls"],
    icon: MapPin,
  },
  {
    name: "Kruunuvuorenranta & Kalasatama",
    tagline: "New waterfront — saunas, sea views, modern blocks",
    vibe: "The new Helsinki: glass towers, harbour saunas, REDI mall, ferries to design islands. Still being built, already photogenic.",
    stayFor: "Repeat visitors who've done the centre. Sea views, modern hotels, easy metro into town.",
    avoidIf: "You want classic European old-town atmosphere.",
    highlights: ["Uusi Sauna", "REDI shopping & rooftop", "Mustikkamaa island", "Kalasatama market"],
    icon: Trees,
  },
];

const FAQS: { q: string; a: string; id: string }[] = [
  {
    id: "best-area-first-time",
    q: "Where is the best area to stay in Helsinki for first-time visitors?",
    a: "Punavuori (Design District) or Kamppi. Both put you within a 10-minute walk of Market Square, the cathedral, the main shopping streets and the harbour ferries. Punavuori has more character and independent cafés; Kamppi has more chain hotels and the easiest airport-train access.",
  },
  {
    id: "safe-at-night",
    q: "Is Helsinki safe to walk around at night?",
    a: "Yes. Helsinki consistently ranks among the safest capital cities in the world. The central neighbourhoods — Punavuori, Kamppi, Kruununhaka, Töölö — are calm and well-lit at night. Kallio's bar streets get rowdy on weekends but are not dangerous; standard city common sense is enough.",
  },
  {
    id: "walk-between-neighborhoods",
    q: "Can you walk between Helsinki's main neighborhoods?",
    a: "Yes. Helsinki's centre is compact: Kamppi to Punavuori is about 10 minutes on foot, Punavuori to Kruununhaka around 20 minutes, and Kallio is 15–20 minutes from the centre. Trams 2, 3 and 4 link almost every neighbourhood in this guide, and a single HSL ticket covers all of them.",
  },
  {
    id: "best-nightlife",
    q: "Which neighborhood is best for nightlife in Helsinki?",
    a: "Kallio, without question. Vaasankatu and the streets around Helsinginkatu have the densest cluster of bars, dive bars, natural-wine spots and live music. Punavuori has a more grown-up cocktail-bar scene, and the centre is mostly hotel bars and clubs.",
  },
  {
    id: "families",
    q: "Where should families stay in Helsinki?",
    a: "Töölö or Kruununhaka. Töölö has parks, the lake walk, the music centre and family-friendly hotels close to Linnanmäki amusement park. Kruununhaka is quiet, central and steps from the Suomenlinna ferry — an easy day trip with kids.",
  },
  {
    id: "couples",
    q: "Where should couples stay in Helsinki?",
    a: "Punavuori for design, restaurants and a sauna evening at Löyly; or Kruununhaka for old-town atmosphere, wine bars and the cathedral on your doorstep. Both feel romantic without being touristy.",
  },
  {
    id: "harbour-sea",
    q: "Is it worth staying near the harbour or sea?",
    a: "If it's your second visit, yes. Kalasatama, Jätkäsaari and Katajanokka have modern waterfront hotels with sea views, harbour saunas and ferry links. On a first trip, central neighbourhoods give you more on foot.",
  },
  {
    id: "areas-to-avoid",
    q: "What area should I avoid in Helsinki?",
    a: "There are no genuinely unsafe areas in central Helsinki. The main thing to avoid is booking a hotel near the central train station on a noisy street if you're a light sleeper — the station area is fine by day but louder at night than Punavuori or Töölö.",
  },
];

export const Route = createFileRoute("/guides/helsinki-neighborhoods")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "where to stay in helsinki, helsinki neighborhoods, helsinki areas, punavuori, kallio, töölö, kruununhaka, best area to stay helsinki" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:image", content: COVER },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: COVER },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESC,
          image: [COVER],
          datePublished: "2026-06-11",
          dateModified: "2026-06-11",
          author: { "@type": "Organization", name: "what Finland" },
          publisher: {
            "@type": "Organization",
            name: "what Finland",
            logo: { "@type": "ImageObject", url: "https://whatfinland.com/icon-512.png" },
          },
          mainEntityOfPage: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: NeighborhoodGuide,
});

function NeighborhoodGuide() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:py-16">
      <header className="mb-10">
        <p className="text-sm uppercase tracking-wider text-navy/60 mb-3">Helsinki Guide</p>
        <h1 className="font-heading text-3xl sm:text-5xl text-navy leading-tight mb-4">
          Helsinki Neighborhood Guide: Where to Stay & Explore
        </h1>
        <p className="text-lg text-navy/80 leading-relaxed">
          Helsinki is small — you can walk most of it in a day — but each district has its own
          rhythm. Here's where locals would tell you to stay, depending on what you actually came for.
        </p>
      </header>

      <section className="mb-10 rounded-2xl bg-navy/5 p-5 sm:p-6">
        <h2 className="font-heading text-xl text-navy mb-3">The 30-second answer</h2>
        <ul className="space-y-2 text-navy/85">
          <li><strong>First time, mid-budget:</strong> Punavuori or Kamppi.</li>
          <li><strong>Nightlife & cheap eats:</strong> Kallio.</li>
          <li><strong>Concerts & museums:</strong> Töölö.</li>
          <li><strong>Photogenic old town:</strong> Kruununhaka.</li>
          <li><strong>Sea views, modern hotel:</strong> Kalasatama / Jätkäsaari.</li>
        </ul>
      </section>

      <section className="space-y-5">
        {HOODS.map((h) => (
          <article key={h.name} className="rounded-2xl border border-navy/10 p-5 sm:p-6 bg-white">
            <header className="mb-3 flex items-start gap-3">
              <span className="shrink-0 rounded-full bg-navy/10 p-2 text-navy">
                <h.icon className="size-5" />
              </span>
              <div>
                <h3 className="font-heading text-2xl text-navy">{h.name}</h3>
                <p className="text-sm text-navy/60">{h.tagline}</p>
              </div>
            </header>
            <p className="text-navy/85 leading-relaxed mb-3">{h.vibe}</p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-3">
              <div className="rounded-xl bg-navy/5 p-3">
                <dt className="text-navy/60 text-xs mb-1">Stay here for</dt>
                <dd className="text-navy">{h.stayFor}</dd>
              </div>
              <div className="rounded-xl bg-navy/5 p-3">
                <dt className="text-navy/60 text-xs mb-1">Skip if</dt>
                <dd className="text-navy">{h.avoidIf}</dd>
              </div>
            </dl>
            <div className="text-sm text-navy/70">
              <strong className="text-navy/80">Don't miss:</strong> {h.highlights.join(" · ")}
            </div>
          </article>
        ))}
      </section>

      <section className="mt-12 mb-10 rounded-2xl bg-navy/5 p-6">
        <h2 className="font-heading text-2xl text-navy mb-3">Explore each area</h2>
        <p className="text-navy/80 mb-4">
          Browse hand-picked restaurants, bars and things to do filtered by neighborhood:
        </p>
        <div className="flex flex-wrap gap-2">
          <Link to="/eat" className="rounded-full bg-white border border-navy/15 px-4 py-2 text-sm text-navy hover:bg-navy/5">Where to Eat</Link>
          <Link to="/do" className="rounded-full bg-white border border-navy/15 px-4 py-2 text-sm text-navy hover:bg-navy/5">Things to Do</Link>
          <Link to="/happening" className="rounded-full bg-white border border-navy/15 px-4 py-2 text-sm text-navy hover:bg-navy/5">What's Happening</Link>
          <Link to="/cities" className="rounded-full bg-white border border-navy/15 px-4 py-2 text-sm text-navy hover:bg-navy/5">All Cities</Link>
        </div>
      </section>

      <section className="mt-12 mb-10" id="faq">
        <h2 className="font-heading text-2xl text-navy mb-4">Frequently Asked Questions</h2>
        <nav aria-label="FAQ topics" className="mb-6 rounded-2xl border border-navy/10 bg-white p-5">
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-navy/80">
            {FAQS.map((f) => (
              <li key={f.id}>
                <a href={`#${f.id}`} className="underline hover:text-navy">{f.q}</a>
              </li>
            ))}
          </ol>
        </nav>
        <div className="space-y-4">
          {FAQS.map((f) => (
            <details key={f.q} id={f.id} className="group rounded-2xl border border-navy/10 bg-white p-5 open:bg-navy/5 scroll-mt-24">
              <summary className="cursor-pointer list-none font-heading text-lg text-navy flex justify-between items-start gap-3">
                <span>{f.q}</span>
                <span className="text-navy/40 group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-navy/85 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>



      <section className="mt-12 mb-10">
        <h2 className="font-heading text-2xl text-navy mb-4">Booking & official resources</h2>
        <ul className="space-y-2 text-sm text-navy/80">
          <li>• <a href="https://www.myhelsinki.fi/en/stay" target="_blank" rel="noopener" className="underline hover:text-navy">MyHelsinki — official accommodation list</a></li>
          <li>• <a href="https://www.visitfinland.com/en/places-to-go/helsinki-region/" target="_blank" rel="noopener" className="underline hover:text-navy">Visit Finland — Helsinki region</a></li>
          <li>• <a href="https://www.hsl.fi/en" target="_blank" rel="noopener" className="underline hover:text-navy">HSL — public transport tickets</a></li>
        </ul>
      </section>

      <section className="mt-12 rounded-2xl bg-navy text-white p-6 sm:p-8">
        <h2 className="font-heading text-2xl mb-3">Plan the rest of your trip</h2>
        <p className="text-white/85 mb-4">
          Pair your neighborhood pick with our 48-hour itinerary and a sauna evening — Helsinki
          works best when food, sauna and walking are stitched into the same day.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/guides/48-hours-in-helsinki" className="rounded-full bg-white text-navy px-5 py-2 font-medium">
            48 Hours in Helsinki
          </Link>
          <Link to="/guides/best-saunas-helsinki" className="rounded-full border border-white/40 px-5 py-2 font-medium">
            Best Saunas
          </Link>
          <Link to="/eat" className="rounded-full border border-white/40 px-5 py-2 font-medium">
            Where to Eat
          </Link>
        </div>
      </section>
    </div>
  );
}
